import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Loader2, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const field = "w-full rounded-xl border border-line bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-blaze";
const lbl = "mb-1 block text-[11px] font-bold uppercase tracking-wider text-slate-500";

export const SendQuoteModal = ({ initial, onClose }) => {
  const q = initial || {};
  const [f, setF] = useState({
    quote_id: q.id || null,
    customer_name: q.customer_name || "",
    email: q.email || "",
    phone: q.phone || "",
    pickup_suburb: q.pickup_suburb || "",
    drop_suburb: q.drop_suburb || "",
    pickup_address: q.pickup_address || "",
    drop_address: q.drop_address || "",
    moving_date: q.moving_date || "",
    preferred_time: q.preferred_time || q.time_window || "",
    bedrooms: q.bedrooms || "",
    property_type: q.property_type || "",
    truck_size: q.truck_size || q.truck || "",
    rate: q.rate || "",
    estimated_hours: q.estimated_hours || "",
    estimated_total: q.estimated_total || "",
    callout_hours: q.callout_hours || "",
    callout_note: q.callout_note || "",
    booking_fee: q.booking_fee || "",
    discount: q.discount || "",
    special_requirements: q.special_requirements || "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const send = async () => {
    if (!f.email || !/.+@.+\..+/.test(f.email)) return toast.error("Enter a valid customer email.");
    setBusy(true);
    try {
      const payload = { ...f };
      ["rate", "estimated_hours", "estimated_total", "callout_hours", "booking_fee", "discount"].forEach((k) => {
        payload[k] = payload[k] === "" ? 0 : parseFloat(payload[k]);
      });
      if (!payload.quote_id) delete payload.quote_id;
      const { data } = await axios.post(`${API}/quotes/send`, payload);
      toast.success(`Quote ${data.quote_number} emailed to ${data.sent_to} ✉️`);
      onClose(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send the quote.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center bg-black/50 p-0 backdrop-blur-sm sm:items-center sm:p-4" data-testid="send-quote-modal">
      <div className="max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-t-3xl bg-white shadow-2xl sm:rounded-3xl">
        <div className="sticky top-0 flex items-center justify-between border-b border-line bg-white px-5 py-4">
          <div>
            <h2 className="font-display text-lg font-bold text-slate-900">{f.quote_id ? "Edit & Send Quote" : "Send a Quote"}</h2>
            <p className="text-xs text-slate-500">Fill the details — a branded quote email goes to the customer.</p>
          </div>
          <button onClick={() => onClose(false)} className="rounded-full p-2 text-slate-400 hover:bg-slate-100" data-testid="send-quote-close"><X className="h-5 w-5" /></button>
        </div>
        <div className="space-y-4 p-5">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><label className={lbl}>Customer name</label><input className={field} value={f.customer_name} onChange={set("customer_name")} data-testid="sq-name" placeholder="John Smith" /></div>
            <div><label className={lbl}>Email *</label><input type="email" className={field} value={f.email} onChange={set("email")} data-testid="sq-email" placeholder="you@email.com" /></div>
            <div><label className={lbl}>Phone</label><input className={field} value={f.phone} onChange={set("phone")} data-testid="sq-phone" placeholder="04XX XXX XXX" /></div>
            <div><label className={lbl}>Pickup suburb</label><input className={field} value={f.pickup_suburb} onChange={set("pickup_suburb")} data-testid="sq-pickup" /></div>
            <div><label className={lbl}>Drop suburb</label><input className={field} value={f.drop_suburb} onChange={set("drop_suburb")} data-testid="sq-drop" /></div>
            <div><label className={lbl}>Moving date</label><input type="date" className={field} value={f.moving_date} onChange={set("moving_date")} data-testid="sq-date" /></div>
            <div><label className={lbl}>Preferred time</label><input className={field} value={f.preferred_time} onChange={set("preferred_time")} placeholder="8:00 AM" data-testid="sq-time" /></div>
            <div><label className={lbl}>Bedrooms</label><input className={field} value={f.bedrooms} onChange={set("bedrooms")} placeholder="2" data-testid="sq-bedrooms" /></div>
            <div><label className={lbl}>Property type</label><input className={field} value={f.property_type} onChange={set("property_type")} placeholder="House" data-testid="sq-proptype" /></div>
            <div><label className={lbl}>Truck</label><input className={field} value={f.truck_size} onChange={set("truck_size")} placeholder="Medium 6T" data-testid="sq-truck" /></div>
            <div><label className={lbl}>Rate ($/hr)</label><input type="number" className={field} value={f.rate} onChange={set("rate")} data-testid="sq-rate" /></div>
            <div><label className={lbl}>Est. hours</label><input type="number" step="0.5" className={field} value={f.estimated_hours} onChange={set("estimated_hours")} data-testid="sq-hours" /></div>
            <div><label className={lbl}>Call-out (hrs)</label><input type="number" step="0.5" className={field} value={f.callout_hours} onChange={set("callout_hours")} placeholder="e.g. 1" data-testid="sq-callout-hours" /></div>
            <div><label className={lbl}>Booking fee ($)</label><input type="number" className={field} value={f.booking_fee} onChange={set("booking_fee")} data-testid="sq-fee" /></div>
            <div><label className={lbl}>Discount (% off rate)</label><input type="number" min="0" max="100" className={field} value={f.discount} onChange={set("discount")} placeholder="0" data-testid="sq-discount" /></div>
          </div>
          {parseFloat(f.callout_hours) > 0 && (
            <div><label className={lbl}>Call-out reason (optional)</label><input className={field} value={f.callout_note} onChange={set("callout_note")} placeholder="e.g. drop-off is 95 km from Melbourne CBD" data-testid="sq-callout-note" /></div>
          )}
          {parseFloat(f.discount) > 0 && parseFloat(f.rate) > 0 && (
            <div className="rounded-xl bg-emerald-50 px-4 py-3 text-sm" data-testid="sq-discount-preview">
              <span className="text-slate-500 line-through">${parseFloat(f.rate)}/hr</span>
              <span className="ml-2 font-bold text-emerald-700">${parseFloat((parseFloat(f.rate) * (1 - parseFloat(f.discount) / 100)).toFixed(2))}/hr</span>
              <span className="ml-2 rounded-full bg-emerald-200 px-2 py-0.5 text-[11px] font-bold text-emerald-800">{parseFloat(f.discount)}% OFF</span>
              <span className="ml-1 text-xs text-slate-500">— discounted hourly rate the customer sees</span>
            </div>
          )}
          <div><label className={lbl}>Notes (optional)</label><textarea rows={2} className={field} value={f.special_requirements} onChange={set("special_requirements")} data-testid="sq-notes" /></div>
          <button onClick={send} disabled={busy} className="flex w-full items-center justify-center gap-2 rounded-xl bg-blaze py-3.5 text-sm font-bold uppercase tracking-widest text-white transition-all hover:bg-blue-700 active:scale-[0.98] disabled:opacity-60" data-testid="sq-send-btn">
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Send className="h-4 w-4" /> Send Quote Email</>}
          </button>
        </div>
      </div>
    </div>
  );
};
